// Formül çekmecesi/editörü (kural 10): varyant listesi + aktif radyo + ifade girişi.
// Canlı `parseFormula`: sözdizimi hatası ifade altında monospace satır + `^` işaretçisi;
// `refs ⊆ codes` — bilinmeyen kod altı çizili + "sette yok"; yazarken kod önerisi
// (`FormulaExpressionField`, jeton bazlı — eski datalist/"Kod ekle" kalktı, 2026-10-08);
// sunucu döngü hatası (KPI_FORMULA_CYCLE) yol olarak ("a → b → a").
// Set editöründe `codes` = set kodları, katalogda = tüm katalog kodları (`scope` metni değişir).
// CEVIR birim seçici: "Birim çevir (CEVIR) ekle" → satır içi popover (gösterge → kaynak birim
// otomatik/salt okunur, yoksa seçilir → yalnız ULAŞILABİLİR hedefler `reachableUnits`) → canlı
// önizleme "1 kWh = 0,0036 GJ" → imleç konumuna `CEVIR(kod; "GJ")` (kaynak gösterge biriminden
// geliyorsa kısa, elle seçildiyse 3 argümanlı). Çevrilemeyen çift satır içi hata; altta
// katlanabilir "Desteklenen çevrimler" tablosu (aile gruplu; para birimi çevrilmez notu).
// TODO-KPI-91: ifadede AGIRLIKLI_ORT varsa (ve `childrenByCode` verildiyse) altında "Eşleme"
// listesi — kalemler SIRAYLA eşlenir ("Kadın maaş ↔ Kadın çalışan sayısı"); sayılar eşit değilse
// sunucu 422'sinin aynı metniyle amber satır (`wavgEditorPreview`, kaydetmeyi engellemez).

import { useId, useRef, useState } from "react";
import { type KpiFormula, type KpiMetricInUseSet } from "@mahir/shared";
import {
  caretLine,
  convertIssues,
  insertSnippetAtCursor,
  validateFormulaDraft,
} from "../../lib/kpi-set-editor";
import { wavgEditorPreview, type WavgChild } from "../../lib/kpi-wavg-pairs";
import { ConvertPicker, HighlightedExpression, SupportedConversions } from "./FormulaConvertPicker";
import { FormulaExpressionField } from "./FormulaExpressionField";
import { FormulaHelpPanel } from "./FormulaHelpPanel";
import { KPI_COPY } from "../../lib/kpi-ui";
import {
  btnSmall,
  errorBox,
  fieldStyle,
  inputStyle,
  labelStyle,
  monoStyle,
} from "./kpi-form-styles";

export interface FormulaServerError {
  message: string;
  /** KPI_FORMULA_CYCLE details.cycle → "a → b → a" */
  cyclePath?: string | null;
  formulaId?: string | null;
  position?: number | null;
  /** Finar rev-B 11 — 409 KPI_METRIC_IN_USE: göstergeyi içeren veri setleri (MetricErrorText) */
  inUseSets?: KpiMetricInUseSet[];
}

export interface FormulaEditorProps {
  formulas: KpiFormula[];
  activeFormulaId: string | null;
  codes: ReadonlySet<string>;
  /** kendi kodu — kendine atıf yerel uyarı */
  ownCode?: string;
  /** kod → birim (set: override ?? metrik birimi; katalog: varsayılan birim) — CEVIR seçici + çevrim kontrolü */
  unitsByCode?: ReadonlyMap<string, string | null>;
  /** kod → etiket — yazarken öneri listesinde ad olarak görünür, etiketten de arar */
  labelsByCode?: ReadonlyMap<string, string>;
  /** TODO-KPI-91: kod → alt kalemler (sırasıyla) — AGIRLIKLI_ORT eşleme önizlemesi */
  childrenByCode?: ReadonlyMap<string, readonly WavgChild[]>;
  /** TODO-KPI-154: göstergenin kendi kırılım grupları — formül yardımında boyut çipi */
  ownDimensions?: readonly { slug: string; label: string }[];
  scope: "set" | "catalog";
  disabled?: boolean;
  serverError?: FormulaServerError | null;
  onChange: (formulas: KpiFormula[], activeFormulaId: string | null) => void;
}

const FUNCTIONS_HINT =
  "Kodlar + − × ÷ ( ) · TOPLA (kategori toplamı) · ORT · TOPLA_ALT / ORT_ALT (alt kalemler) · AGIRLIKLI_ORT · ONCEKI_YIL · DEGISIM_YUZDE · CEVIR · ayırıcı ;";

function newFormula(): KpiFormula {
  return { id: crypto.randomUUID(), label: "Yeni varyant", expression: "", isActive: false };
}

/** İfadeyi kod jetonlarına bölüp bilinmeyenleri altı çizili gösterir. */
export function FormulaEditor(props: FormulaEditorProps): JSX.Element {
  const {
    formulas,
    activeFormulaId,
    codes,
    ownCode,
    unitsByCode,
    labelsByCode,
    childrenByCode,
    ownDimensions,
    scope,
    disabled,
    serverError,
    onChange,
  } = props;
  const listId = useId();
  const scopeText = scope === "set" ? "veri setinde yok" : "katalogda yok";
  const convertCodes = [...codes].sort().filter((c) => c !== ownCode);
  // imleç konumu + input ref'i (varyant id → …): CEVIR parçası imlecin olduğu yere girer
  const cursors = useRef(new Map<string, number>());
  const inputs = useRef(new Map<string, HTMLTextAreaElement>());
  const [convertFor, setConvertFor] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // yardım örnekleri son odaklanan varyanta (yoksa ilk/yeni varyanta) eklenir
  const lastFocused = useRef<string | null>(null);

  function insertExample(example: string): void {
    let idx = formulas.findIndex((f) => f.id === lastFocused.current);
    if (idx < 0) idx = 0;
    const f = formulas[idx];
    if (!f) {
      const nf = newFormula();
      onChange([{ ...nf, isActive: true, expression: example }], nf.id);
      setHelpOpen(false);
      return;
    }
    const r = insertSnippetAtCursor(f.expression, example, cursors.current.get(f.id));
    update(idx, { expression: r.expression });
    cursors.current.set(f.id, r.cursor);
    setHelpOpen(false);
    const el = inputs.current.get(f.id);
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(r.cursor, r.cursor);
      });
    }
  }

  function insertConvert(idx: number, f: KpiFormula, snippet: string): void {
    const r = insertSnippetAtCursor(f.expression, snippet, cursors.current.get(f.id));
    update(idx, { expression: r.expression });
    cursors.current.set(f.id, r.cursor);
    setConvertFor(null);
    const el = inputs.current.get(f.id);
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(r.cursor, r.cursor);
      });
    }
  }

  function update(idx: number, patch: Partial<KpiFormula>): void {
    const next = formulas.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next, activeFormulaId);
  }
  function setActive(id: string | null): void {
    onChange(
      formulas.map((f) => ({ ...f, isActive: f.id === id })),
      id,
    );
  }
  function remove(idx: number): void {
    const removed = formulas[idx];
    const next = formulas.filter((_, i) => i !== idx);
    const nextActive = removed?.id === activeFormulaId ? null : activeFormulaId;
    onChange(next, nextActive);
  }
  function add(): void {
    const f = newFormula();
    const makeActive = formulas.length === 0;
    onChange([...formulas, { ...f, isActive: makeActive }], makeActive ? f.id : activeFormulaId);
  }

  return (
    <div style={fieldStyle}>
      <span style={labelStyle}>
        Formül varyantları <span style={{ textTransform: "none" }}>(biri aktif)</span>
      </span>
      {formulas.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          Henüz varyant yok.
        </div>
      ) : null}
      <div role="radiogroup" aria-label="Aktif formül varyantı" style={{ display: "grid", gap: 8 }}>
        {formulas.map((f, idx) => {
          const check = validateFormulaDraft(f.expression, codes);
          const cvIssues = convertIssues(f.expression, unitsByCode);
          const selfRef = ownCode !== undefined && check.refs.includes(ownCode);
          const srv = serverError?.formulaId === f.id ? serverError : null;
          const isActive = f.id === activeFormulaId;
          return (
            <div
              key={f.id}
              className="rounded-lg p-2"
              style={{
                border: `1px solid ${isActive ? "var(--color-primary)" : "var(--color-border)"}`,
                background: isActive
                  ? "color-mix(in srgb, var(--color-primary) 5%, white)"
                  : "var(--color-surface)",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <div className="flex items-center gap-2">
                <label
                  className="flex items-center gap-1 text-xs"
                  style={{ color: "var(--color-text-2)", minHeight: 32, cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    name={`${listId}-active`}
                    checked={isActive}
                    disabled={disabled}
                    onChange={() => setActive(f.id)}
                    aria-label={`${f.label} aktif`}
                  />
                  aktif
                </label>
                <input
                  aria-label="Varyant adı"
                  style={{ ...inputStyle, flex: 1, minWidth: 0, minHeight: 32, padding: "4px 8px" }}
                  value={f.label}
                  disabled={disabled}
                  onChange={(e) => update(idx, { label: e.target.value })}
                />
                <button
                  type="button"
                  style={{ ...btnSmall, minHeight: 32 }}
                  disabled={disabled}
                  onClick={() => remove(idx)}
                  aria-label={`${f.label} varyantını kaldır`}
                >
                  Kaldır
                </button>
              </div>
              <div className="mt-2">
                <FormulaExpressionField
                  ariaLabel={`${f.label} ifadesi`}
                  value={f.expression}
                  codes={codes}
                  labelsByCode={labelsByCode}
                  unitsByCode={unitsByCode}
                  ownCode={ownCode}
                  disabled={disabled}
                  invalid={!(check.ok && !selfRef)}
                  placeholder="örn. kaza_sayisi * 1000000 / calisma_saati — yazmaya başlayın, kod önerileri açılır"
                  inputRef={(el) => {
                    if (el) inputs.current.set(f.id, el);
                    else inputs.current.delete(f.id);
                  }}
                  onChange={(v) => update(idx, { expression: v })}
                  onCursor={(c) => {
                    cursors.current.set(f.id, c);
                    lastFocused.current = f.id;
                  }}
                />
              </div>
              {!check.ok &&
              check.position !== undefined &&
              !(cvIssues.length > 0 && check.error?.startsWith("Birimler çevrilemez")) ? (
                <pre
                  aria-live="polite"
                  style={{
                    ...monoStyle,
                    margin: "6px 0 0",
                    padding: "6px 8px",
                    background: "var(--color-bg)",
                    borderRadius: 6,
                    color: "var(--color-error)",
                    whiteSpace: "pre",
                    overflowX: "auto",
                  }}
                >
                  {f.expression || " "}
                  {"\n"}
                  {caretLine(check.position)} {check.error}
                </pre>
              ) : null}
              {!check.ok && check.unknownRefs.length > 0 ? (
                <div className="text-xs mt-1" aria-live="polite">
                  <HighlightedExpression
                    expression={f.expression}
                    unknown={check.unknownRefs}
                    scopeText={scopeText}
                  />
                  <div style={{ color: "var(--color-error)" }}>
                    {scopeText}: {check.unknownRefs.join(", ")}
                  </div>
                </div>
              ) : null}
              {selfRef ? (
                <div className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                  Formül kendi koduna ({ownCode}) atıf yapamaz.
                </div>
              ) : null}
              {cvIssues.map((issue) => (
                <div
                  key={`${issue.code}-${issue.from}-${issue.to}`}
                  className="text-xs mt-1"
                  style={{ color: "var(--color-error)" }}
                  aria-live="polite"
                >
                  {issue.message}
                </div>
              ))}
              {childrenByCode ? (
                <WavgPreview
                  groups={wavgEditorPreview(f.expression, childrenByCode, labelsByCode)}
                />
              ) : null}
              <div className="mt-2">
                <button
                  type="button"
                  style={btnSmall}
                  disabled={disabled === true || convertCodes.length === 0}
                  aria-expanded={convertFor === f.id}
                  onClick={() => setConvertFor(convertFor === f.id ? null : f.id)}
                >
                  ⇄ Birim çevir (CEVIR) ekle
                </button>
              </div>
              {convertFor === f.id && !disabled ? (
                <ConvertPicker
                  codes={convertCodes}
                  unitsByCode={unitsByCode}
                  labelsByCode={labelsByCode}
                  onInsert={(snippet) => insertConvert(idx, f, snippet)}
                  onCancel={() => setConvertFor(null)}
                />
              ) : null}
              {srv ? (
                <div className="mt-1" style={errorBox} role="alert">
                  {srv.cyclePath ? (
                    <>
                      Formül döngüsü: <span style={monoStyle}>{srv.cyclePath}</span>
                    </>
                  ) : (
                    srv.message
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {serverError && !serverError.formulaId ? (
        <div style={errorBox} role="alert">
          {serverError.cyclePath ? (
            <>
              Formül döngüsü: <span style={monoStyle}>{serverError.cyclePath}</span>
            </>
          ) : (
            serverError.message
          )}
        </div>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" style={btnSmall} disabled={disabled} onClick={add}>
          ＋ Varyant ekle
        </button>
        <button
          type="button"
          style={btnSmall}
          aria-haspopup="dialog"
          onClick={() => setHelpOpen(true)}
          data-testid="kpi-formula-help"
        >
          ? Formül yardımı
        </button>
      </div>
      <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
        {FUNCTIONS_HINT}
      </div>
      <SupportedConversions />
      <FormulaHelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onInsert={disabled ? undefined : insertExample}
        ownDimensions={ownDimensions}
      />
    </div>
  );
}

/** TODO-KPI-91 — AGIRLIKLI_ORT eşleme listesi (sırayla); uyuşmazlıkta amber satır. */
function WavgPreview(props: { groups: ReturnType<typeof wavgEditorPreview> }): JSX.Element | null {
  if (props.groups.length === 0) return null;
  return (
    <div className="text-xs mt-2" data-testid="kpi-wavg-pairs" aria-live="polite">
      {props.groups.map((g) => (
        <div key={`${g.code};${g.weightCode}`} className="mt-1">
          <div style={{ color: "var(--color-text-2)", fontWeight: 600 }}>
            {KPI_COPY.cell.wavgPairsTitle}
          </div>
          <ul style={{ margin: "2px 0 0 14px", lineHeight: 1.7, color: "var(--color-text-2)" }}>
            {g.pairs.map((p, i) => (
              <li key={i}>
                {p.value?.label ?? "—"} ↔ {p.weight?.label ?? "—"}
              </li>
            ))}
          </ul>
          {g.mismatch ? (
            <div
              className="rounded-md mt-1"
              data-testid="kpi-wavg-mismatch"
              style={{
                padding: "4px 8px",
                color: "var(--color-warning-text)",
                background: "var(--color-warning-bg)",
                border: "1px solid var(--color-warning-border)",
              }}
            >
              {g.mismatch}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
